// One WebSocket for the whole XOmics portal.
//
// The backend publishes invalidation signals on /ws/portal:
//   { type: "invalidate", topic: "jobs", at: 1784… }
// A signal carries no data. It means "this surface changed" -- the subscriber
// refetches through its normal, permission-checked API call. That keeps every
// authorization decision on the REST endpoints where it already lives, and
// means no genomic or patient data ever travels over the socket.
//
// The server registers a socket only under the topics the account's permissions
// entitle it to, and answers the auth frame with the list. So a reviewer never
// receives a cluster signal, and this client never has to know the rules.

import { API_ORIGIN, getAccessToken } from './api';

const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;
const AUTH_GRACE_MS = 10000;

const handlers = new Map();
const statusListeners = new Set();

let socket = null;
let status = 'idle';
let attempt = 0;
let reconnectTimer;
let authTimer;
let openTimer;
let authenticated = false;
let wantConnection = false;
let grantedTopics = [];

function socketUrl() {
  const origin = API_ORIGIN.replace(/^http/, 'ws').replace(/\/$/, '');
  return `${origin}/ws/portal`;
}

function setStatus(next) {
  if (status === next) return;
  status = next;
  statusListeners.forEach((listener) => listener(status));
}

export function getLiveStatus() {
  return status;
}

/** Topics the server said this account may receive. Empty until authenticated. */
export function getGrantedTopics() {
  return grantedTopics;
}

export function onLiveStatus(listener) {
  statusListeners.add(listener);
  listener(status);
  return () => statusListeners.delete(listener);
}

function clearTimers() {
  if (reconnectTimer !== undefined) window.clearTimeout(reconnectTimer);
  if (authTimer !== undefined) window.clearTimeout(authTimer);
  if (openTimer !== undefined) window.clearTimeout(openTimer);
  reconnectTimer = undefined;
  authTimer = undefined;
  openTimer = undefined;
}

function scheduleReconnect() {
  if (!wantConnection || reconnectTimer !== undefined) return;
  // Exponential backoff with jitter, so a backend restart does not produce a
  // synchronised reconnect burst from every open tab.
  const backoff = Math.min(RECONNECT_BASE_MS * (2 ** attempt), RECONNECT_MAX_MS);
  const delay = backoff / 2 + Math.random() * (backoff / 2);
  attempt += 1;
  setStatus('reconnecting');
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = undefined;
    open();
  }, delay);
}

function teardown(reconnect) {
  clearTimers();
  authenticated = false;
  if (socket) {
    const closing = socket;
    socket.onopen = null;
    socket.onmessage = null;
    socket.onerror = null;
    socket.onclose = null;
    if (closing.readyState === WebSocket.OPEN) {
      try { closing.close(); } catch { /* already closing */ }
    } else if (closing.readyState === WebSocket.CONNECTING) {
      // React StrictMode intentionally mounts and immediately unmounts effects
      // once in development. Closing a CONNECTING socket synchronously makes
      // browsers report a failed handshake even though nothing failed. Let the
      // orphan finish its handshake, then close it without handlers.
      closing.addEventListener('open', () => {
        try { closing.close(); } catch { /* already closing */ }
      }, { once: true });
    }
    socket = null;
  }
  if (reconnect) scheduleReconnect();
  else setStatus('idle');
}

function dispatch(topic, at) {
  const set = handlers.get(topic);
  if (!set) return;
  [...set].forEach((handler) => {
    try {
      handler(at);
    } catch (error) {
      // One bad subscriber must never stop the others from revalidating.
      console.warn(`[live] subscriber for "${topic}" threw:`, error);
    }
  });
}

function open() {
  if (!wantConnection) return;
  // No session yet: stay idle rather than opening a socket that would only be
  // closed for failing authentication.
  if (!getAccessToken()) return;
  if (socket) return;

  setStatus(attempt === 0 ? 'connecting' : 'reconnecting');

  let ws;
  try {
    ws = new WebSocket(socketUrl());
  } catch {
    scheduleReconnect();
    return;
  }
  socket = ws;

  ws.onopen = () => {
    // The server closes an unauthenticated socket after five seconds, so send
    // the token immediately. It goes in a frame rather than the URL so it is
    // never written to a proxy or access log.
    const current = getAccessToken();
    if (!current) { teardown(true); return; }
    try {
      ws.send(JSON.stringify({ type: 'auth', token: current }));
    } catch {
      teardown(true);
      return;
    }
    authTimer = window.setTimeout(() => {
      if (!authenticated) teardown(true);
    }, AUTH_GRACE_MS);
  };

  ws.onmessage = (event) => {
    let message;
    try {
      message = JSON.parse(String(event.data));
    } catch {
      return;
    }
    if (message?.type === 'ready') {
      authenticated = true;
      attempt = 0;
      grantedTopics = Array.isArray(message.topics) ? message.topics : [];
      if (authTimer !== undefined) window.clearTimeout(authTimer);
      authTimer = undefined;
      setStatus('live');
      return;
    }
    if (message?.type === 'invalidate' && typeof message.topic === 'string') {
      dispatch(message.topic, typeof message.at === 'number' ? message.at : Date.now());
    }
  };

  ws.onerror = () => { /* onclose always follows; handled there */ };

  ws.onclose = () => {
    // 4003 means the presented token expired. api.js refreshes on the next
    // request, and the caller restarts this with the new one.
    socket = null;
    grantedTopics = [];
    teardown(true);
  };
}

/** Subscribe to a topic. Returns an unsubscribe function. */
export function subscribeTopic(topic, handler) {
  let set = handlers.get(topic);
  if (!set) {
    set = new Set();
    handlers.set(topic, set);
  }
  set.add(handler);
  return () => {
    const current = handlers.get(topic);
    if (!current) return;
    current.delete(handler);
    if (current.size === 0) handlers.delete(topic);
  };
}

/**
 * Opens the socket and keeps it open. Call again after the signed-in account
 * changes: the server fixes a socket's entitlement when it authenticates, so a
 * different account needs a different socket.
 */
export function startLiveUpdates() {
  wantConnection = true;
  attempt = 0;
  teardown(false);
  wantConnection = true;
  // Defer construction by one task. StrictMode's development-only cleanup can
  // then cancel the first mount before a network connection exists.
  openTimer = window.setTimeout(() => {
    openTimer = undefined;
    open();
  }, 0);

  // A tab woken from sleep often has a socket the OS already killed without
  // firing onclose. Revalidate whenever it becomes visible again.
  const onVisible = () => {
    if (!wantConnection) return;
    if (document.visibilityState === 'hidden') return;
    if (!socket || socket.readyState === WebSocket.CLOSED) {
      attempt = 0;
      open();
    }
  };
  window.addEventListener('focus', onVisible);
  document.addEventListener('visibilitychange', onVisible);

  return () => {
    wantConnection = false;
    window.removeEventListener('focus', onVisible);
    document.removeEventListener('visibilitychange', onVisible);
    teardown(false);
  };
}
